import { FilterToolbar, PageCanvas } from "@/components/page-canvas";
import { Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  RecordDescription,
  RecordMetadata,
  RecordRow,
  RecordTitle,
} from "@/components/record-link";
import {
  locationDetailingProductsPath,
  locationDetailingProductPath,
  newLocationDetailingProductPath,
} from "@/routes";
import { quantity, type Product } from "./types";

export default function Inventory({
  products,
  low_stock_only,
  query,
  stock_summary,
}: {
  products: Product[];
  low_stock_only: boolean;
  query: string;
  stock_summary: { products: number; empty: number; low: number };
}) {
  const { auth } = usePage().props;
  const { t, i18n } = useTranslation();
  if (!auth.location) return null;
  const location = auth.location;
  return (
    <AppLayout
      breadcrumbs={[
        {
          title: t("inventory.title"),
          href: locationDetailingProductsPath(location.id),
        },
      ]}
    >
      <Head title={t("inventory.title")} />
      <PageCanvas>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-page font-display">{t("inventory.title")}</h1>
          {auth.user?.role === "admin" && (
            <Button
              render={
                <Link href={newLocationDetailingProductPath(location.id)} />
              }
            >
              {t("inventory.add")}
            </Button>
          )}
        </div>
        <p className="text-muted-foreground text-sm">
          {t("inventory.local_help", { location: location.name })}
        </p>
        <dl className="grid gap-4 rounded-lg border p-4 sm:grid-cols-3">
          {(["products", "low", "empty"] as const).map((key) => (
            <div key={key}>
              <dt className="text-sm text-muted-foreground">
                {t(`workflow.stock_${key}`)}
              </dt>
              <dd className="text-xl font-semibold">{stock_summary[key]}</dd>
            </div>
          ))}
        </dl>
        <FilterToolbar
          action={locationDetailingProductsPath(location.id)}
          method="get"
          columns="search"
        >
          {low_stock_only && <input type="hidden" name="low_stock" value="1" />}
          <div className="space-y-2">
            <Label htmlFor="inventory-search">
              {t("workflow.search_products")}
            </Label>
            <Input id="inventory-search" name="q" defaultValue={query} />
          </div>
          <Button
            variant="outline"
            type="submit"
            className="justify-self-start"
          >
            {t("requests.filter")}
          </Button>
        </FilterToolbar>
        <nav aria-label={t("inventory.title")} className="flex flex-wrap gap-3">
          <Button
            variant={low_stock_only ? "outline" : "default"}
            render={
              <Link
                href={locationDetailingProductsPath(location.id, { q: query })}
                aria-current={!low_stock_only ? "page" : undefined}
              />
            }
          >
            {t("inventory.all")}
          </Button>
          <Button
            variant={low_stock_only ? "default" : "outline"}
            render={
              <Link
                href={locationDetailingProductsPath(location.id, {
                  low_stock: "1",
                  q: query,
                })}
                aria-current={low_stock_only ? "page" : undefined}
              />
            }
          >
            {t("inventory.low_only")}
          </Button>
        </nav>
        <Card>
          <CardContent className="divide-y">
            {products.length === 0 && (
              <p className="text-muted-foreground py-6">
                {t(low_stock_only ? "inventory.low_empty" : "inventory.empty")}
              </p>
            )}
            {products.map((product) => (
              <RecordRow
                key={product.id}
                className="-mx-3 py-5"
                href={locationDetailingProductPath(location.id, product.id)}
              >
                <div className="flex flex-wrap items-center gap-3">
                  <RecordTitle>{product.name}</RecordTitle>
                  {(product.low_stock || Number(product.quantity) === 0) && (
                    <Badge
                      variant="destructive"
                      className="h-auto whitespace-normal py-1 text-sm font-semibold"
                    >
                      {[
                        product.low_stock ? t("inventory.low") : null,
                        Number(product.quantity) === 0
                          ? t("workflow.stock_empty")
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </Badge>
                  )}
                </div>
                <RecordDescription className="mt-2">
                  <span className="text-muted-foreground">
                    {t("inventory.balance")}:{" "}
                  </span>
                  <span className="font-semibold tabular-nums">
                    {quantity(product.quantity, i18n.language)}{" "}
                    {t(`inventory.${product.unit}`)}
                  </span>
                </RecordDescription>
                <RecordMetadata className="mt-1">
                  {t("inventory.threshold")}:{" "}
                  {product.threshold === null
                    ? t("inventory.disabled")
                    : `${quantity(product.threshold, i18n.language)} ${t(`inventory.${product.unit}`)}`}
                </RecordMetadata>
              </RecordRow>
            ))}
          </CardContent>
        </Card>
      </PageCanvas>
    </AppLayout>
  );
}
